// packingListApi.ts — the contract module for the packing-list reader (038).
//
// Same convention as poApi.ts, poImportApi.ts, poScanApi.ts and quoteApi.ts:
// every API-shape assumption for this flow lives here, read off
// api/src/procurement/packing-list-service.js and packing-list-ai.js.
//
// ── WHY IT LIVES UNDER Receiving/ AND NOT Procurement/ ──────────────────────
// The three sibling lanes are purchasing flows and their contracts sit beside the
// purchase-order screens. This one ends in the receiving hub's drawer: its output
// is a prefill for `POST /api/inventory/receive`, and the types it has to line up
// with are Receiving/types.ts's. The API route is still under /procurement/
// because that is where the AI document lanes are mounted, which is a backend
// arrangement rather than a statement about whose screen this is.
//
// ── WHAT IS SHARED, AND WHAT IS THIS LANE'S OWN ────────────────────────────
// The review-entry shape and `reviewKey` come from the review kit
// (components/review/grounding), because the gate is literally the same server
// code. The holding-cell row shapes come from poScanApi, because migrations 018
// and 038 put all three lanes in one table. The extraction is this lane's own: a
// slip has no PO number of its own, no prices worth committing to, a shipped
// quantity that may disagree with what was ordered, and a per-line link to OUR
// purchase order.

import { useApi } from '../apiHelpers';
import type { ImportWarning } from '../Procurement/poImportApi';
import type { ReviewTarget, ConfirmReviewInput } from '../components/review/grounding';
import { draftPageUrl } from '../Procurement/poScanApi';

export type { ImportWarning } from '../Procurement/poImportApi';
export { reviewKey } from '../components/review/grounding';
export type { ScanDraftSummary as PackingListDraftSummary, ScanDraftFile } from '../Procurement/poScanApi';
export { isDraftImage } from '../Procurement/poScanApi';

/** How the slip reached us. Photos are pages of ONE slip, in page order. */
export type PackingListSource = 'packing_list_pdf' | 'packing_list_photo';

/**
 * How confident the alignment of one slip line to one order line is.
 *
 * Only `exact_part_number` is silent. Every other value raises a mandatory review,
 * because every other value is a guess or an absence, and a quantity attached to
 * the wrong order line is a wrong receipt against a real commitment.
 */
export type PackingListMatchReason =
  /** The slip's part number resolved, and exactly one open order line wants it. */
  | 'exact_part_number'
  /** It resolved, but two or more open lines want it. A person picks. */
  | 'ambiguous'
  /** No part number resolved; one order line's wording covers the description. */
  | 'description'
  /** The only order line for this item is already fully received. */
  | 'line_already_received'
  /** Nothing. Kept anyway: an unordered arrival is a fact worth recording. */
  | 'unmatched';

/** One open order line a slip line could be attached to. */
export interface PackingListCandidate {
  purchase_order_line_id: string;
  purchase_order_id: string | null;
  po_number: string | null;
  line_number: number | null;
  part_catalog_id: number | null;
  sku: string | null;
  part_description: string | null;
  // From the catalog_item_names view (migration 068).
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  quantity_expected: number;
  quantity_received: number;
  /** Expected minus received: what this line is still waiting for. */
  remaining: number;
  /** STRIPPED without can_financial — what we agreed to pay is not on their slip. */
  unit_cost_cents?: number | null;
  status: string;
}

export interface PackingListLine {
  /** Position on the SLIP as printed, so screen and paper agree. */
  line_number: number;
  description: string | null;
  part_number: string | null;
  /** What the shipper says is IN THE BOX, never what was ordered. */
  quantity_shipped: number;
  lot_number: string | null;
  /**
   * The price the SLIP printed, when it printed one. EVIDENCE, not a cost: it is
   * never used to value stock, and the drawer's cost field stays locked to the
   * order. Not stripped for a non-financial reader, because it is printed on the
   * document in their hands.
   */
  unit_price_cents_observed: number | null;
  /** A per-row purchase order reference, on a slip whose item table carries one. */
  po_number: string | null;
  po_number_key: string | null;

  // Filled by the server's alignment pass.
  purchase_order_line_id: string | null;
  purchase_order_id: string | null;
  part_catalog_id: number | null;
  sku_guess: string | null;
  match_reason: PackingListMatchReason | null;
  /** Token overlap, 0..1, only on a `description` match. */
  match_score?: number;
  candidates: PackingListCandidate[];

  /** What the matched order line still expects, for the over-shipment check. */
  matched_remaining: number | null;
  /** STRIPPED without can_financial. See PackingListCandidate.unit_cost_cents. */
  matched_unit_cost_cents?: number | null;
  matched_po_number: string | null;

  included: boolean;
  /** The reader's own "I could not read this". Becomes a mandatory review. */
  ai_not_sure: boolean;
}

/** A purchase order number the slip names, as printed and as normalized. */
export interface PackingListPoRef {
  key: string;
  observed: string | null;
  /** True when it appeared only in the item table, never in a header block. */
  from_rows_only: boolean;
}

export interface PackingListExtraction {
  slip_number: string | null;
  ship_date: string | null;
  currency: string | null;
  prices_present: boolean;
  po_numbers: PackingListPoRef[];
  vendor: { name: string | null };
  vendor_key: string | null;
  ship_to_name: string | null;
  lines: PackingListLine[];
  line_count: number;
}

export interface PackingListConfidence {
  slip_number: number;
  ship_date: number;
  po_numbers: number;
  vendor: number;
  lines: number;
}

/** The shared reasons, plus the six only a packing list can produce. */
export type PackingListReviewReason =
  | 'low_confidence'
  | 'unverified_text'
  | 'ungrounded_scan'
  | 'ai_remark'
  | 'model_not_sure'
  | 'vendor_is_self'
  | 'prices_dropped_currency'
  | 'po_number_unknown'
  | 'ambiguous_match'
  | 'description_match'
  | 'unmatched_line'
  | 'line_already_received'
  | 'quantity_over_ordered'
  | 'price_variance';

export interface PackingListReviewEntry {
  target: ReviewTarget;
  field: string | null;
  line_number: number | null;
  reason: PackingListReviewReason;
  message: string;
}

/** A purchase order the slip named that we actually have. */
export interface PackingListPurchaseOrder {
  id: string;
  po_number: string;
  status: string;
  vendor_id: string | null;
  vendor_name: string | null;
}

export interface PackingListVendorResolution {
  vendor_id: string | null;
  vendor_name: string | null;
  /**
   * 'purchase_order' when the named orders agreed on one supplier, which is more
   * reliable than the letterhead and is why it is tried first. 'document_name'
   * when the slip's own name matched a vendor row. Null when neither.
   */
  source: 'purchase_order' | 'document_name' | null;
  document_name: string | null;
}

export interface PackingListParseResponse {
  ok: true;
  instance: 'firefly' | 'swarm';
  source_filename: string | null;
  source: PackingListSource;
  extraction: PackingListExtraction;
  confidence: PackingListConfidence;
  warnings: ImportWarning[];
  purchase_orders: PackingListPurchaseOrder[];
  ref_only_line_count: number;
  /** Named on the slip, absent here. Their lines have nothing to match against. */
  unknown_po_numbers: string[];
  vendor_resolution: PackingListVendorResolution;
  candidate_line_count: number;
  review_required: PackingListReviewEntry[];
  /**
   * The holding-cell row this read was staged as, so the review survives
   * navigating away. Null when the stage failed, which is not fatal: the read
   * still came back, it just has no safety net.
   */
  draft_id?: number | null;
}

// ---------------------------------------------------------------------------
// The holding cell
// ---------------------------------------------------------------------------

export const PACKING_LIST_DRAFTS_PATH = '/procurement/packing-list/drafts';

/**
 * An object URL for one page of a staged slip, or null.
 *
 * The same helper the other two lanes use, pointed at this lane's route. THE
 * CALLER OWNS THE REVOKE; leaking these pins every page ever viewed in memory for
 * the life of the tab.
 */
export const packingListDraftPageUrl = (draftId: number, page: number): Promise<string | null> =>
  draftPageUrl(draftId, page, PACKING_LIST_DRAFTS_PATH);

export interface PackingListDraft {
  id: number;
  created_at: string;
  created_by_name: string | null;
  source: PackingListSource;
  vendor_guess: string | null;
  /** The SLIP's number, despite the column name. See migration 038. */
  po_number_guess: string | null;
  /** The parse response VERBATIM — the review screen hydrates straight from it. */
  staged_doc: PackingListParseResponse;
  files: { page_number: number; mimetype: string; size_bytes: number }[];
}

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

export interface PackingListConfirmLineInput {
  line_number: number;
  purchase_order_line_id: string | null;
  part_catalog_id: number | null;
  part_number: string | null;
  description: string | null;
  quantity_shipped: number;
  lot_number: string | null;
  unit_price_cents_observed: number | null;
  included?: boolean;
}

export interface PackingListConfirmInput {
  packing_list: {
    slip_number: string | null;
    ship_date: string | null;
    vendor_id: string | null;
    vendor_observed: string | null;
    source: PackingListSource;
    source_filename: string | null;
    notes?: string | null;
    lines: PackingListConfirmLineInput[];
  };
  // Echoed for the same reason as every other lane: the server RECOMPUTES the
  // needs-review list from these three rather than trusting a client-supplied one,
  // so a payload cannot shorten its own homework.
  staged_extraction?: PackingListExtraction;
  staged_confidence?: PackingListConfidence;
  staged_warnings?: ImportWarning[];
  reviews?: ConfirmReviewInput[];
  draft_id?: number | null;
}

export interface PackingListConfirmResult {
  ok: true;
  packing_list_id: string;
  slip_number: string | null;
  vendor_id: string | null;
  line_count: number;
  /** How many of those lines have an order line to be received against. */
  matched_line_count: number;
  /** The distinct purchase orders those matched lines belong to. */
  purchase_order_ids: string[];
}

// ---------------------------------------------------------------------------
// The prefill feed
// ---------------------------------------------------------------------------

/**
 * One confirmed slip line that still has boxes owing.
 *
 * This is what puts "the slip says 40" on a receiving inbox row. A line leaves
 * this feed when the boxes received against it sum to its shipped quantity, which
 * is DERIVED rather than stored — see migration 038 on why there is no status
 * column.
 */
export interface PendingSlipLine {
  id: string;
  packing_list_id: string;
  purchase_order_line_id: string;
  quantity_shipped: number;
  quantity_boxed: number;
  quantity_outstanding: number;
  lot_number: string | null;
  unit_price_cents_observed: number | null;
  part_number_observed: string | null;
  description_observed: string | null;
  slip_number: string | null;
  ship_date: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
}

/** One box that came in against a slip line. */
export interface PackingListBox {
  id: string;
  packing_list_line_id: string;
  barcode: string;
  quantity_received: number;
  quantity_remaining: number;
  /** 'accepted' | 'rejected' (037). A rejected box is at QC Hold, not on a shelf. */
  disposition: string;
  received_at: string;
  lot_number: string | null;
  location_name: string | null;
  location_type: string | null;
}

/** One row of the shipment history list. */
export interface PackingListSummary {
  id: string;
  slip_number: string | null;
  ship_date: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_observed: string | null;
  source: PackingListSource;
  source_filename: string | null;
  created_at: string;
  line_count: number;
  /** How many lines have all their boxes in. Derived, never stored. */
  lines_boxed: number;
  /** The purchase orders this shipment was against, deduped. */
  po_numbers: string[];
}

/** One confirmed shipment, read back long after its draft is gone. */
export interface PackingListRecordLine {
  id: string;
  line_number: number;
  purchase_order_line_id: string | null;
  part_catalog_id: number | null;
  part_number_observed: string | null;
  description_observed: string | null;
  quantity_shipped: number;
  lot_number: string | null;
  unit_price_cents_observed: number | null;
  sku: string | null;
  part_description: string | null;
  // From the catalog_item_names view (migration 068).
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  quantity_expected: number | null;
  quantity_received: number | null;
  ordered_unit_cost_cents?: number | null;
  order_line_status: string | null;
  po_number: string | null;
  /** Summed off the boxes below. The three numbers that matter are ordered vs
   *  shipped vs this. */
  quantity_boxed: number;
  boxes: PackingListBox[];
}

export interface PackingListRecord {
  id: string;
  slip_number: string | null;
  ship_date: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_observed: string | null;
  source: PackingListSource;
  source_filename: string | null;
  notes: string | null;
  created_at: string;
  created_by_firstname: string | null;
  created_by_lastname: string | null;
  lines: PackingListRecordLine[];
}

export function usePackingListApi() {
  const api = useApi();

  return {
    /**
     * Read ONE packing list. `files` is a single PDF, or up to eight photos of the
     * pages of one slip.
     *
     * Every page goes up under the SAME field name, in array order: the server
     * treats a repeated `file` field as the pages of one document, and page order
     * is upload order. Two PDFs in one request are refused rather than batched.
     */
    parse: (files: File | File[]): Promise<PackingListParseResponse> => {
      const fd = new FormData();
      for (const file of Array.isArray(files) ? files : [files]) fd.append('file', file);
      return api.post('/procurement/packing-list/parse', { body: fd });
    },

    /** Record the shipment. Receives NOTHING: the drawer still does that. */
    confirm: (body: PackingListConfirmInput): Promise<PackingListConfirmResult> =>
      api.post('/procurement/packing-list/confirm', { body }),

    /** Confirmed slip lines still waiting for boxes. The hub's prefill feed. */
    pendingLines: (): Promise<{ rows: PendingSlipLine[] }> =>
      api.get('/procurement/packing-list/pending-lines'),

    /** Everything read but not yet confirmed, oldest first. Communal. */
    listDrafts: (): Promise<{ drafts: import('../Procurement/poScanApi').ScanDraftSummary[] }> =>
      api.get(PACKING_LIST_DRAFTS_PATH),

    /** One staged slip, with the parse response to hydrate the screen from. */
    getDraft: (id: number): Promise<PackingListDraft> => api.get(`${PACKING_LIST_DRAFTS_PATH}/${id}`),

    /** Throw a mis-read away. Its pages go with it. */
    discardDraft: (id: number): Promise<void> =>
      api.delete(`${PACKING_LIST_DRAFTS_PATH}/${id}`, { skipJsonParse: true }),

    /** One confirmed shipment, with what was ordered, said and boxed. */
    get: (id: string): Promise<PackingListRecord> => api.get(`/procurement/packing-list/${id}`),

    /**
     * The shipment history, newest first.
     *
     * `q` matches a slip number, a supplier name, or a purchase order number — the
     * three strings somebody has in hand when a vendor phones about a delivery.
     */
    list: (opts: { q?: string; limit?: number } = {}): Promise<{ rows: PackingListSummary[] }> => {
      const params = new URLSearchParams();
      if (opts.q) params.set('q', opts.q);
      if (opts.limit) params.set('limit', String(opts.limit));
      const qs = params.toString();
      return api.get(`/procurement/packing-list${qs ? `?${qs}` : ''}`);
    },
  };
}
